import type { UsageProfile } from '@web/core/auth/auth-api';
import {
  PERIOD_PRESETS,
  REPORT_BUCKETS,
  REPORT_TABS,
  type PeriodPreset,
  type ReportBucket,
  type ReportFilters,
  type ReportTab,
} from '../contract';

/**
 * The state of `/reports` in the URL (spec 014 rule 2):
 * `?tab=sales&period=30d&from=&to=&bucket=&unit=&team=&owner=&pipeline=&connection=`.
 */
export interface ReportsSearch {
  tab?: ReportTab;
  period?: PeriodPreset;
  from?: string;
  to?: string;
  /** The granularity of the series (none: the API picks by the length). */
  bucket?: ReportBucket;
  unit?: string;
  team?: string;
  owner?: string;
  pipeline?: string;
  /** A WhatsApp number (the WhatsApp tab). */
  connection?: string;
}

export type ReportNarrowing = 'unit' | 'team' | 'owner' | 'pipeline' | 'connection';

/** The filters each tab answers: the WhatsApp spend has no team or owner (spec 027). */
/**
 * The filters each tab takes (spec 045, rule 18): the pipeline only in sales, the owner where
 * a person answers for the numbers (sales and service).
 */
export function narrowingsOf(tab: ReportTab): ReportNarrowing[] {
  if (tab === 'sales') return ['unit', 'team', 'owner', 'pipeline'];
  if (tab === 'service' || tab === 'tickets') return ['unit', 'team', 'owner'];
  if (tab === 'whatsapp') return ['unit', 'connection'];
  return ['unit', 'team'];
}

const isDay = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : undefined);

export function parseReportsSearch(search: Record<string, unknown>): ReportsSearch {
  const out: ReportsSearch = {};
  if ((REPORT_TABS as readonly unknown[]).includes(search['tab']))
    out.tab = search['tab'] as ReportTab;
  if ((PERIOD_PRESETS as readonly unknown[]).includes(search['period'])) {
    out.period = search['period'] as PeriodPreset;
  }
  if (isDay(search['from'])) out.from = search['from'];
  if (isDay(search['to'])) out.to = search['to'];
  if ((REPORT_BUCKETS as readonly unknown[]).includes(search['bucket'])) {
    out.bucket = search['bucket'] as ReportBucket;
  }
  for (const key of ['unit', 'team', 'owner', 'pipeline', 'connection'] as const) {
    const value = text(search[key]);
    if (value) out[key] = value;
  }
  return out;
}

export function shiftDay(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** The days between two days, both counted. */
export function daysBetween(from: string, to: string): number {
  return (
    Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1
  );
}

/** The days of a preset, counted from `today` (a day in the organization's zone). */
export function presetRange(preset: PeriodPreset, today: string): { from: string; to: string } {
  const month = today.slice(0, 7);
  switch (preset) {
    case '7d':
      return { from: shiftDay(today, -6), to: today };
    case '90d':
      return { from: shiftDay(today, -89), to: today };
    case 'year':
      return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case '12m':
      return { from: shiftDay(today, -364), to: today };
    case 'month':
      return { from: `${month}-01`, to: today };
    case 'lastMonth': {
      const firstOfThis = `${month}-01`;
      const lastOfPrevious = shiftDay(firstOfThis, -1);
      return { from: `${lastOfPrevious.slice(0, 7)}-01`, to: lastOfPrevious };
    }
    default:
      return { from: shiftDay(today, -29), to: today };
  }
}

/**
 * The tab a search without one opens: Sales, or Service for an organization that only does
 * service (spec 044).
 */
export function defaultReportTab(profile: UsageProfile): ReportTab {
  return profile === 'service' ? 'service' : 'sales';
}

/** The API filters of a search (custom days when both are set, else the preset). */
export function reportFilters(
  search: ReportsSearch,
  today: string,
  defaultTab: ReportTab = 'sales',
): ReportFilters {
  const preset = search.period ?? '30d';
  const range =
    preset === 'custom' && search.from && search.to && search.from <= search.to
      ? { from: search.from, to: search.to }
      : presetRange(preset === 'custom' ? '30d' : preset, today);
  const applies = narrowingsOf(search.tab ?? defaultTab);
  const pick = (key: ReportNarrowing) => (applies.includes(key) ? search[key] : undefined);
  const unit = pick('unit');
  const team = pick('team');
  const owner = pick('owner');
  const pipeline = pick('pipeline');
  const connection = pick('connection');
  return {
    ...range,
    ...(unit ? { unitId: unit } : {}),
    ...(team ? { teamId: team } : {}),
    ...(owner ? { ownerMembershipId: owner } : {}),
    ...(pipeline ? { pipelineId: pipeline } : {}),
    ...(connection ? { connectionId: connection } : {}),
    ...(search.bucket ? { bucket: search.bucket } : {}),
  };
}
